import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  ChartBarIcon,
  TrendingUpIcon,
  StarIcon,
  DocumentTextIcon,
  FilterIcon
} from '@heroicons/react/outline';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const CommissionReports = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString()
      .split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    insuranceTypeId: '',
    companyId: '',
    branchId: ''
  });

  const [reportData, setReportData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [topPerformers, setTopPerformers] = useState(null);

  const [insuranceTypes, setInsuranceTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const [typesRes, companiesRes, branchesRes] = await Promise.all([
        api.get('/api/admin/insurance-types'),
        api.get('/api/companies'),
        api.get('/api/branches')
      ]);
      setInsuranceTypes(typesRes.data);
      setCompanies(companiesRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      
      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate
      };
      if (filters.insuranceTypeId) params.insuranceTypeId = filters.insuranceTypeId;
      if (filters.companyId) params.companyId = filters.companyId;
      if (filters.branchId) params.branchId = filters.branchId;

      const [reportRes, analyticsRes, trendsRes, performersRes] = await Promise.all([
        api.get('/api/admin/commissions/report', { params }),
        api.get('/api/admin/commissions/analytics', { params: { startDate: filters.startDate, endDate: filters.endDate } }),
        api.get('/api/admin/commissions/trends', { params: { months: 6 } }),
        api.get('/api/admin/commissions/top-performers', { params })
      ]);

      setReportData(reportRes.data);
      setAnalytics(analyticsRes.data);
      setTrends(trendsRes.data);
      setTopPerformers(performersRes.data);
      
      toast.success(t('commissions.reportGenerated'));
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-LY', {
      style: 'currency',
      currency: 'LYD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ar-LY').format(num || 0);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">{t('commissions.title')}</h1>
        <p className="text-gray-600 mt-1">{t('commissions.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <FilterIcon className="w-5 h-5 text-gray-600" />
          <h2 className="text-xl font-semibold text-gray-800">{t('commissions.filterBy')}</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('commissions.startDate')}
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('commissions.endDate')}
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('commissions.insuranceType')}
            </label>
            <select
              value={filters.insuranceTypeId}
              onChange={(e) => setFilters({ ...filters, insuranceTypeId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('commissions.allTypes')}</option>
              {insuranceTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('commissions.company')}
            </label>
            <select
              value={filters.companyId}
              onChange={(e) => setFilters({ ...filters, companyId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('commissions.allCompanies')}</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('commissions.branch')}
            </label>
            <select
              value={filters.branchId}
              onChange={(e) => setFilters({ ...filters, branchId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('commissions.allBranches')}</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>{branch.name}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="mt-4">
          <button
            onClick={handleGenerateReport}
            disabled={loading}
            className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                {t('common.loading')}
              </>
            ) : (
              <>
                <ChartBarIcon className="w-5 h-5" />
                {t('commissions.generateReport')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {reportData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">{t('commissions.totalDocuments')}</p>
                  <p className="text-3xl font-bold mt-1">
                    {formatNumber(reportData.summary.totalDocuments)}
                  </p>
                </div>
                <DocumentTextIcon className="w-12 h-12 opacity-20" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-md p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">{t('commissions.totalCommission')}</p>
                  <p className="text-2xl font-bold mt-1">
                    {formatCurrency(reportData.summary.totalCommission)}
                  </p>
                </div>
                <ChartBarIcon className="w-12 h-12 opacity-20" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-md p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">{t('commissions.avgCommission')}</p>
                  <p className="text-2xl font-bold mt-1">
                    {formatCurrency(reportData.summary.avgCommission)}
                  </p>
                </div>
                <TrendingUpIcon className="w-12 h-12 opacity-20" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-md p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">{t('commissions.minCommission')}</p>
                  <p className="text-2xl font-bold mt-1">
                    {formatCurrency(reportData.summary.minCommission)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-md p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">{t('commissions.maxCommission')}</p>
                  <p className="text-2xl font-bold mt-1">
                    {formatCurrency(reportData.summary.maxCommission)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Analytics Charts */}
          {analytics && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* By Insurance Type */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  {t('commissions.byInsuranceType')}
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analytics.byInsuranceType.map((item) => ({
                        name: item.insuranceType?.name || 'Unknown',
                        value: parseFloat(item.totalCommission)
                      }))}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.name}: ${formatCurrency(entry.value)}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {analytics.byInsuranceType.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* By Company */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  {t('commissions.byCompany')}
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.byCompany.map((item) => ({
                    name: item.company?.name || 'Unknown',
                    commission: parseFloat(item.totalCommission),
                    docs: parseInt(item.documentCount)
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => 
                        name === 'commission' ? formatCurrency(value) : formatNumber(value)
                      }
                      labelFormatter={(label) => `Company: ${label}`}
                    />
                    <Legend />
                    <Bar dataKey="commission" fill="#3B82F6" name={t('commissions.commissionAmount')} />
                    <Bar dataKey="docs" fill="#10B981" name={t('commissions.documentCount')} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Trends */}
          {trends && trends.overall.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <TrendingUpIcon className="w-5 h-5" />
                {t('commissions.monthlyTrends')}
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trends.overall.map((item) => ({
                  month: item.month,
                  commission: parseFloat(item.totalCommission),
                  docs: parseInt(item.documentCount)
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip 
                    formatter={(value, name) => 
                      name === 'commission' ? formatCurrency(value) : formatNumber(value)
                    }
                  />
                  <Legend />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="commission" 
                    stroke="#3B82F6" 
                    strokeWidth={2}
                    name={t('commissions.commissionAmount')}
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="docs" 
                    stroke="#10B981" 
                    strokeWidth={2}
                    name={t('commissions.documentCount')}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Top/Bottom Performers */}
          {topPerformers && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Companies */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <StarIcon className="w-5 h-5 text-yellow-500" />
                  {t('commissions.topCompanies')}
                </h3>
                <div className="space-y-3">
                  {topPerformers.topCompanies.map((item, index) => (
                    <div key={item.companyId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className={`text-2xl font-bold ${index === 0 ? 'text-yellow-500' : index === 1 ? 'text-gray-400' : 'text-orange-500'}`}>
                          #{index + 1}
                        </span>
                        <div>
                          <p className="font-semibold text-gray-800">
                            {item.company?.name || 'Unknown'}
                          </p>
                          <p className="text-sm text-gray-600">
                            {formatNumber(item.documentCount)} {t('commissions.documentCount')}
                          </p>
                        </div>
                      </div>
                      <p className="text-lg font-bold text-green-600">
                        {formatCurrency(item.totalCommission)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Branches */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <StarIcon className="w-5 h-5 text-yellow-500" />
                  {t('commissions.topBranches')}
                </h3>
                <div className="space-y-3">
                  {topPerformers.topBranches.map((item, index) => (
                    <div key={item.branchId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className={`text-2xl font-bold ${index === 0 ? 'text-yellow-500' : index === 1 ? 'text-gray-400' : 'text-orange-500'}`}>
                          #{index + 1}
                        </span>
                        <div>
                          <p className="font-semibold text-gray-800">
                            {item.branch?.name || 'Unknown'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {item.company?.name}
                          </p>
                          <p className="text-sm text-gray-600">
                            {formatNumber(item.documentCount)} {t('commissions.documentCount')}
                          </p>
                        </div>
                      </div>
                      <p className="text-lg font-bold text-green-600">
                        {formatCurrency(item.totalCommission)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Empty State */}
      {!reportData && !loading && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <ChartBarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">
            {t('commissions.noReportGenerated')}
          </h3>
          <p className="text-gray-500">
            {t('commissions.selectFiltersAndGenerate')}
          </p>
        </div>
      )}
    </div>
  );
};

export default CommissionReports;

